import classNames from 'classnames'
import { AlertTriangle, Check, Clock, Loader2, X, type LucideIcon } from 'lucide-react'
import React from 'react'

export type BulkActionStatusPhase =
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'partially_succeeded'
    | 'failed'
    | 'conflict'

export interface BulkActionStatusCounts {
    total?: number
    completed?: number
    succeeded?: number
    failed?: number
    skipped?: number
}

/**
 * Past-tense verb for a successful item, for example `created` or `deleted`.
 * The outcome summary and the per-item pills must agree, so both read it from
 * the same place rather than hard-coding one action's wording.
 */
export type BulkActionVerb = 'updated' | 'created' | 'deleted'

export interface BulkActionStatusProps {
    phase: BulkActionStatusPhase
    /**
     * `undefined` uses the phase default. Pass `null` when the dialog header
     * already states the outcome, so the card does not repeat it.
     */
    title?: React.ReactNode | null
    message?: React.ReactNode
    counts?: BulkActionStatusCounts
    verb?: BulkActionVerb
    /** Singular entity for the outcome line, for example `product ad`. */
    entity?: string
    /** Plural entity. Defaults to `${entity}s`. */
    entityPlural?: string
    /** Omit when the backend cannot provide meaningful determinate progress. */
    progressPercent?: number
    className?: string
}

const statusDefaults: Record<
    BulkActionStatusPhase,
    { title: string; icon: LucideIcon; accent: string }
> = {
    queued: {
        title: 'Queued',
        icon: Clock,
        accent: 'text-[#2d72d2]',
    },
    running: {
        title: 'Bulk action in progress',
        icon: Loader2,
        accent: 'text-[#2d72d2]',
    },
    succeeded: {
        title: 'Bulk action complete',
        icon: Check,
        accent: 'text-[#1d7046]',
    },
    partially_succeeded: {
        title: 'Bulk action partially complete',
        icon: AlertTriangle,
        accent: 'text-[#8a4f0f]',
    },
    failed: {
        title: 'Bulk action failed',
        icon: X,
        accent: 'text-[#a82a2f]',
    },
    conflict: {
        title: 'Some items changed before this action ran',
        icon: AlertTriangle,
        accent: 'text-[#8a4f0f]',
    },
}

const clampProgress = (progress: number) => Math.min(100, Math.max(0, progress))

export function BulkActionStatus({
    phase,
    title,
    message,
    counts,
    verb = 'updated',
    entity,
    entityPlural,
    progressPercent,
    className,
}: BulkActionStatusProps) {
    const presentation = statusDefaults[phase]
    const Icon = presentation.icon
    const isActive = phase === 'queued' || phase === 'running'
    const isRunning = phase === 'running'
    const progress = progressPercent === undefined ? undefined : clampProgress(progressPercent)

    return (
        <section
            aria-live="polite"
            aria-busy={isActive}
            className={classNames(
                'rounded-[6px] border border-[#d8e0e8] bg-white px-[14px] py-[18px]',
                className,
            )}
        >
            <div className="flex items-start gap-[10px]">
                {/* The rail matches the icon so the mark never eats the card padding. */}
                <div className="flex w-[18px] flex-none justify-center">
                    <Icon
                        aria-hidden="true"
                        className={classNames(
                            'mt-[2px] h-[18px] w-[18px] flex-none',
                            isRunning && 'animate-spin',
                            presentation.accent,
                        )}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    {title !== null && (
                        <h3 className="m-0 text-[16px] font-semibold leading-[22px] text-[#1c2127]">
                            {title ?? presentation.title}
                        </h3>
                    )}
                    {message && (
                        <div
                            className={classNames(
                                'text-[14px] leading-[20px] text-[#5f6b7c]',
                                title !== null && 'mt-[4px]',
                            )}
                        >
                            {message}
                        </div>
                    )}
                    {isActive && (
                        <div className="mt-[14px]">
                            {progress === undefined ? (
                                <div
                                    role="progressbar"
                                    aria-label="Bulk action progress"
                                    className="h-[7px] overflow-hidden rounded-full bg-[#d5e5f5]"
                                >
                                    <div className="h-full w-[42%] animate-pulse rounded-full bg-[#2d72d2]" />
                                </div>
                            ) : (
                                <div
                                    role="progressbar"
                                    aria-label="Bulk action progress"
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-valuenow={progress}
                                    className="h-[7px] overflow-hidden rounded-full bg-[#d5e5f5]"
                                >
                                    <div
                                        className="h-full rounded-full bg-[#2d72d2] transition-[width] duration-300"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {counts && phase !== 'running' && (
                        <BulkActionCountSummary
                            counts={counts}
                            verb={verb}
                            entity={entity}
                            entityPlural={entityPlural}
                        />
                    )}
                </div>
            </div>
        </section>
    )
}

/**
 * The whole outcome is one short line: "24 product ads created. 3 skipped".
 *
 * The leading outcome names the entity, so this line can stand alone as the
 * card body. Zero-valued outcomes are dropped; a run where every outcome is
 * zero gets explicit wording rather than a string of zeros.
 */
export function BulkActionCountSummary({
    counts,
    verb = 'updated',
    entity,
    entityPlural = entity ? `${entity}s` : undefined,
}: {
    counts: BulkActionStatusCounts
    verb?: BulkActionVerb
    entity?: string
    entityPlural?: string
}) {
    const outcomes = [
        { label: verb, value: counts.succeeded, tone: 'text-[#1d7046]' },
        { label: 'failed', value: counts.failed, tone: 'text-[#a82a2f]' },
        { label: 'skipped', value: counts.skipped, tone: 'text-[#8a4f0f]' },
    ].filter((entry): entry is { label: string; value: number; tone: string } =>
        Number.isFinite(entry.value as number),
    )
    const reported = outcomes.filter((entry) => entry.value > 0)

    if (outcomes.length === 0) {
        const processed = counts.completed
        const total = counts.total

        if (!Number.isFinite(total as number)) {
            return null
        }

        return (
            <p className="m-0 mt-[10px] text-[14px] leading-[20px] text-[#5f6b7c]">
                {Number.isFinite(processed as number)
                    ? `${(processed as number).toLocaleString()} of ${(total as number).toLocaleString()} processed`
                    : `${(total as number).toLocaleString()} items`}
            </p>
        )
    }

    const noun = entityPlural ?? 'items'
    // "1 portfolios failed" reads as a bug, so a leading count of one takes the
    // singular when the caller supplied it.
    const nounFor = (value: number) => (value === 1 && entity ? entity : noun)

    return (
        <p className="m-0 mt-[10px] text-[14px] leading-[20px] text-[#5f6b7c]">
            {reported.length === 0
                ? `No ${noun} were changed.`
                : reported.map((entry, index) => (
                      <React.Fragment key={entry.label}>
                          {index > 0 && '. '}
                          <span className={classNames('font-semibold tabular-nums', entry.tone)}>
                              {entry.value.toLocaleString()}
                          </span>{' '}
                          {/* Only the leading outcome names the entity. */}
                          {index === 0 && entityPlural
                              ? `${nounFor(entry.value)} ${entry.label}`
                              : entry.label}
                      </React.Fragment>
                  ))}
        </p>
    )
}

export type BulkActionResultItemStatus = 'succeeded' | 'failed' | 'skipped'

export interface BulkActionResultItem {
    id: string
    label: React.ReactNode
    status: BulkActionResultItemStatus
    message?: React.ReactNode
}

export interface BulkActionResultDetailsProps {
    items: readonly BulkActionResultItem[]
    title?: React.ReactNode
    emptyMessage?: React.ReactNode
    verb?: BulkActionVerb
    /**
     * Total outcomes the run produced. Pass it whenever `items` is a subset, so
     * a short list cannot read as the complete picture.
     */
    total?: number
    className?: string
}

const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

const resultStatusLabel = (status: BulkActionResultItemStatus, verb: BulkActionVerb) =>
    status === 'succeeded' ? capitalise(verb) : status === 'failed' ? 'Failed' : 'Skipped'

export function BulkActionResultDetails({
    items,
    title = 'Details',
    emptyMessage = 'No item-level details were returned.',
    verb = 'updated',
    total,
    className,
}: BulkActionResultDetailsProps) {
    const truncated = typeof total === 'number' && total > items.length

    return (
        <section className={classNames('mt-[20px]', className)}>
            <div className="mb-[8px] flex flex-wrap items-baseline justify-between gap-x-[12px]">
                <h3 className="m-0 text-[14px] font-semibold text-[#1c2127]">{title}</h3>
                {truncated && (
                    <span className="text-[13px] leading-[18px] text-[#5f6b7c]">
                        Showing {items.length.toLocaleString()} of {total.toLocaleString()}
                    </span>
                )}
            </div>
            {items.length === 0 ? (
                <p className="m-0 rounded-[6px] border border-[#d8e0e8] px-[14px] py-[12px] text-[14px] text-[#738091]">
                    {emptyMessage}
                </p>
            ) : (
                <ul className="m-0 max-h-[240px] list-none overflow-y-auto rounded-[6px] border border-[#d8e0e8] p-0">
                    {items.map((item) => (
                        <li
                            key={item.id}
                            className="flex items-start justify-between gap-[16px] border-b border-[#e7ebef] px-[14px] py-[11px] last:border-b-0"
                        >
                            <div className="min-w-0">
                                <div className="break-words text-[14px] font-medium text-[#1c2127]">
                                    {item.label}
                                </div>
                                {item.message && (
                                    <div className="mt-[2px] break-words text-[12px] leading-[17px] text-[#738091]">
                                        {item.message}
                                    </div>
                                )}
                            </div>
                            <span
                                className={classNames(
                                    'flex-none rounded-full px-[8px] py-[3px] text-[11px] font-semibold',
                                    item.status === 'succeeded' && 'bg-[#d7f0e3] text-[#1d7046]',
                                    item.status === 'failed' && 'bg-[#f8d7d9] text-[#a82a2f]',
                                    item.status === 'skipped' && 'bg-[#f6e7cc] text-[#8a4f0f]',
                                )}
                            >
                                {resultStatusLabel(item.status, verb)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}
